import { useSnackbar } from '@hue/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { getApiError, preferencesApi, type SessionUser, type UserAppearance } from '../lib/api.js';
import { sessionQuery } from '../lib/session.js';

type ColorMode = UserAppearance['mode'];
type PaletteName = UserAppearance['palette'];
type Density = UserAppearance['density'];

/// Quanto esperar a pessoa parar de mexer antes de gravar: trocar de paleta três vezes seguidas é
/// uma escolha só, e a trilha de auditoria não precisa das duas intermediárias.
const SAVE_DELAY_MS = 600;

export const PALETTES: Array<{
  value: PaletteName;
  label: string;
  description: string;
  swatches: [string, string, string];
}> = [
  {
    value: 'hue',
    label: 'HUE',
    description: 'Dourado discreto',
    swatches: ['#0a0a0c', '#d69a24', '#f9f9f7'],
  },
  {
    value: 'mono',
    label: 'Mono',
    description: 'Cinza, branco e preto',
    swatches: ['#111214', '#71717a', '#fafafa'],
  },
  {
    value: 'navy',
    label: 'Navy',
    description: 'Azul profundo e ardósia',
    swatches: ['#08111f', '#537496', '#f5f7fa'],
  },
  {
    value: 'moss',
    label: 'Moss',
    description: 'Verde musgo e sálvia',
    swatches: ['#101710', '#63785f', '#f6f7f3'],
  },
  {
    value: 'wine',
    label: 'Wine',
    description: 'Vinho e vermelho queimado',
    swatches: ['#1b0e11', '#a65d63', '#faf5f5'],
  },
  {
    value: 'straw',
    label: 'Straw',
    description: 'Ocre e palha discreta',
    swatches: ['#17140c', '#a58a4e', '#faf8f1'],
  },
  {
    value: 'blossom',
    label: 'Blossom',
    description: 'Rosa antigo e malva',
    swatches: ['#190f14', '#936579', '#faf5f7'],
  },
];

function initialMode(): ColorMode {
  const stored = localStorage.getItem('hue-theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function initialPalette(): PaletteName {
  const stored = localStorage.getItem('hue-palette');
  return PALETTES.some((palette) => palette.value === stored) ? (stored as PaletteName) : 'hue';
}

function initialDensity(): Density {
  return localStorage.getItem('hue-density') === 'compact' ? 'compact' : 'comfortable';
}

type Choice = Pick<UserAppearance, 'mode' | 'palette' | 'density'>;

/// A escolha que está valendo neste navegador, uma só para quem a mostrar.
///
/// O ícone de cores e a página de Aparência editam a mesma preferência (ADR 0041, decisão 4). Com
/// um estado em cada um, trocar a paleta num deixaria o outro mostrando a antiga até a gravação
/// voltar da API — dois controles discordando sobre a mesma tela. A escolha e a gravação pendente
/// moram aqui, fora dos componentes; quando o último que a mostra sai, ela é esquecida e o próximo
/// relê o navegador e a sessão, como antes.
const appearanceStore = {
  current: null as Choice | null,
  listeners: new Set<() => void>(),
  pending: undefined as { timer: ReturnType<typeof setTimeout>; flush: () => void } | undefined,
  read(): Choice {
    this.current ??= { mode: initialMode(), palette: initialPalette(), density: initialDensity() };
    return this.current;
  },
  write(next: Choice) {
    this.current = next;
    document.documentElement.dataset.theme = next.mode;
    document.documentElement.dataset.palette = next.palette;
    document.documentElement.dataset.density = next.density;
    localStorage.setItem('hue-theme', next.mode);
    localStorage.setItem('hue-palette', next.palette);
    localStorage.setItem('hue-density', next.density);
    for (const listener of this.listeners) listener();
  },
  flushPending() {
    if (!this.pending) return;
    clearTimeout(this.pending.timer);
    this.pending.flush();
  },
  subscribe(listener: () => void) {
    appearanceStore.listeners.add(listener);
    return () => {
      appearanceStore.listeners.delete(listener);
      if (appearanceStore.listeners.size > 0) return;
      // Sair da tela com uma escolha ainda na espera grava na hora, em vez de perdê-la.
      appearanceStore.flushPending();
      appearanceStore.current = null;
    };
  },
};

/// Função estável: com uma nova a cada render, o React desinscreveria e inscreveria de novo a cada
/// pintura, e a última saída esqueceria a escolha no meio do caminho.
function subscribeToAppearance(listener: () => void) {
  return appearanceStore.subscribe(listener);
}

/// Sem `user` (login, recuperação de senha, ativação) a escolha fica só neste navegador. Com
/// sessão, a escolha gravada na pessoa manda, e cada mudança volta para ela; o navegador continua
/// guardando uma cópia para a próxima tela de login já abrir do jeito certo.
export function useAppearance(user: SessionUser | undefined) {
  const saved = user?.appearance ?? null;
  const choice = useSyncExternalStore(
    subscribeToAppearance,
    () => appearanceStore.read(),
    () => appearanceStore.read(),
  );
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();

  // A sessão pode chegar depois da tela (ou mudar noutro aparelho e voltar num refetch). Enquanto
  // houver gravação pendente, o que está na tela é mais novo que a sessão e não pode ser desfeito.
  useEffect(() => {
    if (!saved || appearanceStore.pending) {
      appearanceStore.write(appearanceStore.read());
      return;
    }
    appearanceStore.write({ mode: saved.mode, palette: saved.palette, density: saved.density });
  }, [saved?.mode, saved?.palette, saved?.density]); // eslint-disable-line react-hooks/exhaustive-deps

  function choose(change: Partial<Choice>) {
    const next: Choice = { ...appearanceStore.read(), ...change };
    appearanceStore.write(next);
    if (!user) return;
    if (appearanceStore.pending) clearTimeout(appearanceStore.pending.timer);
    const flush = () => {
      appearanceStore.pending = undefined;
      preferencesApi.updateUserAppearance(next).then(
        ({ appearance }) =>
          queryClient.setQueryData(sessionQuery.queryKey, (current) =>
            current
              ? { ...current, appearance: { ...current.appearance, ...appearance } }
              : current,
          ),
        (requestError: unknown) => snackbar.failure(getApiError(requestError)),
      );
    };
    appearanceStore.pending = { timer: setTimeout(flush, SAVE_DELAY_MS), flush };
  }

  return { ...choice, choose };
}
